import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmdialogComponent } from '@shared/components/confirmdialog/confirmdialog.component';
import { aciesDateFormat } from '@shared/helpers';
import { GenericTableColumn } from '@shared/models/generictablecolumn';
import { NotificationService } from '@shared/services/notification.service';
import { Subject, takeUntil } from 'rxjs';
import { IKeepMarksDialogData, KeepMarksDialogComponent } from '../keep-marks-dialog/keep-marks-dialog.component';
import { IReplaySkippedDialogData, ReplaySkippedDialogComponent } from '../replay-skipped-dialog/replay-skipped-dialog.component';
import { AUDIT_FINDING_KINDS, auditStatusOf, auditStatusRank, groupMarks, hasEngineTable, IMarkGroup, skippedCount } from '../sync-monitoring-audit.utils';
import { IAuditRow, IEnvironmentStatusResponse, ITableCleanupDetail, ITableErrorModel } from '../sync-monitoring.models';
import { SyncMonitoringService } from '../sync-monitoring.service';
import { extractTableName } from '../sync-monitoring.utils';

const NS = 'administration.sync-monitoring.audit';

/** One kind of findings of the expanded table, page by page. */
interface IFindingsState {
	kind: string;
	items: ITableErrorModel[];
	total: number | null;
	next: string | null;
	loading: boolean;
	failed: boolean;
}

/** The Audit tab: each table's last completed drift audit, worst first, with its findings, clean-ups and repair actions when expanded. */
@Component({
	selector: 'app-audit-tab',
	templateUrl: './audit-tab.component.html',
	styleUrls: ['./audit-tab.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class AuditTabComponent implements OnChanges, OnDestroy {
	@Input() environmentId: string | null = null;
	@Input() environmentName = '';
	/** The caller holds one:ManualCleanup, needed to audit, replay, keep marked items and reset repair attempts. */
	@Input() canManage = false;
	/** Superadmin: Reset Sync. */
	@Input() canResetSync = false;

	/** The status response the rows are built from; a refresh keeps the expanded table open. */
	@Input() status: IEnvironmentStatusResponse | null = null;
	/** A table to open, e.g. from "Show audit findings" on the Tables tab. */
	@Input() focusTable: { tableName: string } | null = null;

	/** Something changed on the server: the page should reload the status. */
	@Output() changed = new EventEmitter<void>();
	/** Start a drift audit of these tables, or of every table when empty (the page owns that dialog). */
	@Output() auditRequested = new EventEmitter<string[]>();
	/** Reset Sync for this table (the page owns that dialog). */
	@Output() resetRequested = new EventEmitter<string>();

	public tableSource = new MatTableDataSource<IAuditRow>();
	public markedTotal = 0;
	public replayEnabled: boolean | null = null;
	public removalMode: string | null = null;
	public sweepMode: string | null = null;
	public sweepWindowDays: number | null = null;
	public expandedRow: IAuditRow | null = null;
	public findings: IFindingsState[] = [];
	public cleanup: ITableCleanupDetail | null = null;
	public cleanupLoading = false;
	public cleanupFailed = false;
	public markGroups: IMarkGroup[] = [];
	public selectedMarks = new Set<string>();

	public readonly columns: GenericTableColumn[] = [
		{ name: 'administration.sync-monitoring.tables.columns.name', dataKey: 'tableName', isSortable: true },
		{ name: `${NS}.columns.status`, dataKey: 'statusLabel' },
		{ name: `${NS}.columns.missing`, dataKey: 'missing', isSortable: true },
		{ name: `${NS}.columns.mismatch`, dataKey: 'mismatch', isSortable: true },
		{ name: `${NS}.columns.extra`, dataKey: 'extra', isSortable: true },
		{ name: `${NS}.columns.off-version`, dataKey: 'offVersion', isSortable: true },
		{ name: `${NS}.columns.exhausted`, dataKey: 'exhausted', isSortable: true },
		{ name: `${NS}.columns.last-audit`, dataKey: 'lastAudit' },
	];

	private destroy$ = new Subject<void>();
	private pendingFocus: string | null = null;
	private detailRequest = 0;

	constructor(
		private syncMonitoringService: SyncMonitoringService,
		private notificationService: NotificationService,
		private dialog: MatDialog,
		private translateService: TranslateService,
	) {
		// The rows' labels are translated when they are built, so build them again in the new language.
		this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
			const expanded = this.expandedRow?.tableName;
			this.tableSource.data = this.buildRows(this.status);
			this.expandedRow = this.tableSource.data.find((row) => row.tableName === expanded) ?? null;
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['status']) {
			this.tableSource.data = this.buildRows(this.status);
			this.markedTotal = (this.status?.tables ?? []).reduce((sum, table) => sum + (table.cleanup?.sweepReport?.stale ? 0 : (table.cleanup?.sweepReport?.pendingExpiryCount ?? 0)), 0);
			const previous = this.expandedRow;
			const expanded = this.tableSource.data.find((row) => row.tableName === previous?.tableName);
			if (!expanded) {
				this.collapse();
			} else {
				this.expandedRow = expanded;
				// The table's audit or clean-ups changed since its detail was loaded: load it again.
				if (JSON.stringify([expanded.audit, expanded.cleanup]) !== JSON.stringify([previous?.audit, previous?.cleanup])) {
					this.loadDetail(expanded);
				}
			}
		}
		if (changes['focusTable']) {
			this.pendingFocus = this.focusTable?.tableName ?? null;
		}
		this.expandFocus();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	rowClassFn = (row: IAuditRow): string[] => [`sm-audit-${row.status}`, row.tableName === this.expandedRow?.tableName ? 'sm-audit-expanded' : ''];

	onRowExpandToggle(row: IAuditRow): void {
		if (this.expandedRow?.tableName === row.tableName) {
			this.collapse();
		} else {
			this.expandedRow = row;
			this.loadDetail(row);
		}
	}

	// --- Actions -----------------------------------------------------------------

	openReplay(row: IAuditRow): void {
		if (!this.canManage || !this.environmentId) {
			return;
		}
		const data: IReplaySkippedDialogData = {
			environmentId: this.environmentId,
			environmentName: this.environmentName,
			table: { tableName: row.tableName, auditRunId: row.audit?.runId ?? '' },
		};
		this.dialog
			.open(ReplaySkippedDialogComponent, { data, width: '640px' })
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((response) => {
				if (response) {
					const queued = response.tables.filter((table) => table.queued).length;
					this.notificationService.notify([`${NS}.replay-queued`], [`${NS}.replay-queued-text`, { count: queued }], 'success');
					this.changed.emit();
				}
			});
	}

	/** Resets the exhausted repair attempts of one item, or of every exhausted item of the table when `itemKey` is omitted. */
	resetAttempts(row: IAuditRow, itemKey?: string): void {
		const environmentId = this.environmentId;
		if (!this.canManage || !environmentId) {
			return;
		}
		this.dialog
			.open(ConfirmdialogComponent, {
				width: '440px',
				data: {
					heading: this.translateService.instant(`${NS}.reset-attempts.title`),
					content: this.translateService.instant(itemKey ? `${NS}.reset-attempts.item` : `${NS}.reset-attempts.table`, { key: itemKey, table: row.tableName }),
					okButtonText: this.translateService.instant(`${NS}.reset-attempts.confirm`),
					cancelButtonText: this.translateService.instant('general.cancel'),
				},
			})
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((confirmed) => {
				if (!confirmed) {
					return;
				}
				this.syncMonitoringService
					.resetCleanupAttempts(environmentId, row.tableName, itemKey)
					.pipe(takeUntil(this.destroy$))
					.subscribe({
						next: (response) => {
							this.notificationService.notify([`${NS}.reset-attempts.done`, { count: response.reset ?? 0 }], [], 'success');
							this.reloadDetail(row);
						},
						error: () => this.notificationService.notify([`${NS}.reset-attempts.failed`], [], 'error'),
					});
			});
	}

	toggleMark(key: string, checked: boolean): void {
		if (checked) {
			this.selectedMarks.add(key);
		} else {
			this.selectedMarks.delete(key);
		}
	}

	selectedIn(group: IMarkGroup): string[] {
		return group.marks.map((mark) => mark.itemKey).filter((key) => this.selectedMarks.has(key));
	}

	/** Opens the keep dialog for the selected items of a group, or all its items. */
	keepMarks(row: IAuditRow, group: IMarkGroup, all: boolean): void {
		const keys = all ? group.marks.map((mark) => mark.itemKey) : this.selectedIn(group);
		if (!this.canManage || !this.environmentId || keys.length === 0) {
			return;
		}
		const marked = row.cleanup?.sweepReport?.pendingExpiryCount ?? this.markGroups.reduce((sum, g) => sum + g.marks.length, 0);
		const data: IKeepMarksDialogData = {
			environmentId: this.environmentId,
			tableName: row.tableName,
			version: group.version,
			runId: group.runId,
			expiresAt: group.expiresAt,
			keys,
			otherCount: Math.max(0, marked - keys.length),
		};
		this.dialog
			.open(KeepMarksDialogComponent, { data, width: '640px' })
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((response) => {
				if (response) {
					const cleared = response.cleared?.length ?? 0;
					const notCleared = response.notCleared?.length ?? 0;
					this.notificationService.notify([notCleared ? `${NS}.keep-partly` : `${NS}.keep-done`], [notCleared ? `${NS}.keep-partly-text` : `${NS}.keep-done-text`, { cleared, notCleared }], notCleared ? 'neutral' : 'success');
					this.reloadDetail(row);
				}
			});
	}

	loadMore(state: IFindingsState): void {
		if (this.expandedRow && state.next) {
			this.loadFindings(this.expandedRow, state, state.next);
		}
	}

	/** Copies the S3 object version the audit compared, a debugging handle for the item's payload. */
	copyVersion(version: string): void {
		navigator.clipboard?.writeText(version).then(
			() => this.notificationService.notify([`${NS}.copied`], [], 'success'),
			() => this.notificationService.notify([`${NS}.copy-failed`], [`${NS}.copy-failed-text`, { version }], 'error'),
		);
	}

	// --- Template helpers --------------------------------------------------------

	formatEpoch(ms: number | null | undefined): string {
		return ms != null ? aciesDateFormat(new Date(ms), true, true) : '—';
	}

	rowVersion(value: number | null | undefined): string {
		return value != null ? `0x${Math.trunc(value).toString(16).toUpperCase()}` : '—';
	}

	/** Exhausted items first, then the ones still being retried. */
	attemptItems(detail: ITableCleanupDetail): NonNullable<NonNullable<ITableCleanupDetail['retrying']>['items']> {
		return [...(detail.exhausted?.items ?? []), ...(detail.retrying?.items ?? [])];
	}

	isPinned(kind: string): boolean {
		return kind === 'auditDriftMissingInEngine' || kind === 'auditDriftRowVersionMismatch';
	}

	// --- Loading -----------------------------------------------------------------

	private buildRows(status: IEnvironmentStatusResponse | null): IAuditRow[] {
		this.replayEnabled = status?.audit?.settings?.replay?.enabled ?? null;
		this.removalMode = status?.audit?.settings?.removal?.mode ?? null;
		this.sweepMode = status?.audit?.settings?.sweep?.mode ?? null;
		this.sweepWindowDays = status?.audit?.settings?.sweep?.windowDays ?? null;
		// Tables with no engine table (One only) are never audited, so the tab leaves them out.
		return (status?.tables ?? [])
			.filter(hasEngineTable)
			.map((table): IAuditRow => {
				const audit = table.audit ?? null;
				const state = auditStatusOf(audit);
				const comparable = audit?.completedAt != null;
				return {
					tableName: extractTableName(table.self),
					status: state,
					statusLabel: this.translateService.instant(`${NS}.statuses.${state}`),
					missing: comparable ? (audit?.missingCount ?? 0) : null,
					mismatch: comparable ? (audit?.mismatchedCount ?? 0) : null,
					extra: comparable ? (audit?.extraCount ?? 0) : null,
					offVersion: comparable ? (audit?.offVersionCount ?? 0) : null,
					repairLabel: comparable
						? this.translateService.instant(`${NS}.repair-summary`, {
								replayed: audit?.replayedCount ?? 0,
								failed: audit?.failedCount ?? 0,
								deferred: audit?.deferredCount ?? 0,
							})
						: '',
					exhausted: comparable ? (audit?.exhaustedCount ?? 0) : null,
					lastAudit: audit?.completedAt != null ? aciesDateFormat(new Date(audit.completedAt), true, true) : '',
					audit,
					cleanup: table.cleanup ?? null,
					skipped: skippedCount(audit, this.replayEnabled),
				};
			})
			.sort((a, b) => auditStatusRank(a.status) - auditStatusRank(b.status) || (b.exhausted ?? 0) - (a.exhausted ?? 0) || a.tableName.localeCompare(b.tableName));
	}

	private expandFocus(): void {
		const row = this.pendingFocus && this.tableSource.data.find((candidate) => candidate.tableName === this.pendingFocus);
		if (!row) {
			return;
		}
		this.pendingFocus = null;
		if (this.expandedRow?.tableName !== row.tableName) {
			this.expandedRow = row;
			this.loadDetail(row);
		}
		setTimeout(() => document.querySelector('.sm-audit-expanded')?.scrollIntoView({ block: 'center' }), 0);
	}

	private collapse(): void {
		this.expandedRow = null;
		this.findings = [];
		this.cleanup = null;
		this.markGroups = [];
		this.selectedMarks.clear();
		this.detailRequest++;
	}

	/** After an action on the table: reload its detail and let the page reload the status. */
	private reloadDetail(row: IAuditRow): void {
		if (this.expandedRow?.tableName === row.tableName) {
			this.loadDetail(row);
		}
		this.changed.emit();
	}

	private loadDetail(row: IAuditRow): void {
		const request = ++this.detailRequest;
		const audit = row.audit;
		this.findings = audit ? AUDIT_FINDING_KINDS.filter((entry) => (entry.count(audit) ?? 0) > 0 || (entry.kind === 'auditRepairExhausted' && (row.cleanup?.exhausted?.total ?? 0) > 0)).map((entry) => ({ kind: entry.kind, items: [], total: null, next: null, loading: true, failed: false })) : [];
		this.findings.forEach((state) => this.loadFindings(row, state, null, request));

		this.cleanup = null;
		this.cleanupFailed = false;
		this.markGroups = [];
		this.selectedMarks.clear();
		if (!this.environmentId || !row.cleanup || row.cleanup.available === false) {
			return;
		}
		this.cleanupLoading = true;
		this.syncMonitoringService
			.getTableCleanup(this.environmentId, row.tableName)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (detail) => {
					if (request === this.detailRequest) {
						this.cleanup = detail;
						this.markGroups = groupMarks(detail.marks);
						this.cleanupLoading = false;
					}
				},
				error: () => {
					if (request === this.detailRequest) {
						this.cleanupLoading = false;
						this.cleanupFailed = true;
					}
				},
			});
	}

	private loadFindings(row: IAuditRow, state: IFindingsState, after: string | null, request = this.detailRequest): void {
		if (!this.environmentId) {
			return;
		}
		state.loading = true;
		this.syncMonitoringService
			.getAuditFindings(this.environmentId, row.tableName, state.kind, after)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (page) => {
					if (request === this.detailRequest) {
						state.items = [...state.items, ...page.items];
						state.total = page.total ?? state.total;
						state.next = page.next;
						state.loading = false;
					}
				},
				error: () => {
					if (request === this.detailRequest) {
						state.loading = false;
						state.failed = true;
					}
				},
			});
	}
}
